import React, { useEffect, useState } from 'react';
import { Factory, Info, Sparkles } from 'lucide-react';
import { SifaBetreuungsgruppe, SifaBetriebsdaten } from '../../types';
import { BETREUUNGSGRUPPEN, ersthelferSoll, vorschlagEinsatzzeit, zahlAusEingabe } from './sifaBetriebsdatenLogic';

interface Props {
  sifa: SifaBetriebsdaten | undefined;
  /**
   * EIN Update je Aufruf (Muster handleMetadataDirectChange in
   * ProjectMasterData): der Aufrufer mischt den Patch in metadata.sifa.
   * Textfelder rufen es nur beim Verlassen des Feldes und nur bei Aenderung —
   * zwei Tastendruecke erzeugen also nie zwei Schreibvorgaenge.
   */
  onPatch: (patch: Partial<SifaBetriebsdaten>) => void;
  isReadOnly?: boolean;
}

type Entwurf = {
  branche: string;
  berufsgenossenschaft: string;
  bgMitgliedsnummer: string;
  beschaeftigte: string;
  einsatzzeitSollStunden: string;
  betriebsarzt: string;
  sicherheitsbeauftragte: string;
  ersthelfer: string;
};

const zahlText = (n: number | undefined) => (n === undefined || n === null ? '' : String(n));

const entwurfAus = (s: SifaBetriebsdaten | undefined): Entwurf => ({
  branche: s?.branche || '',
  berufsgenossenschaft: s?.berufsgenossenschaft || '',
  bgMitgliedsnummer: s?.bgMitgliedsnummer || '',
  beschaeftigte: zahlText(s?.beschaeftigte),
  einsatzzeitSollStunden: zahlText(s?.einsatzzeitSollStunden),
  betriebsarzt: s?.betriebsarzt || '',
  sicherheitsbeauftragte: s?.sicherheitsbeauftragte || '',
  ersthelfer: zahlText(s?.ersthelfer),
});

const inputClass = 'w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-teal-500 outline-none bg-white text-sm';
const labelClass = 'text-[11px] font-bold text-slate-400 uppercase';

/**
 * Block „Betrieb" der Stammdaten bei SiFa — ersetzt Bau-Kategorie, Bauart,
 * Projektvolumen und Bauherr. Alle Werte liegen in metadata.sifa
 * (SifaBetriebsdaten). Vorschlaege (Einsatzzeit, Ersthelfer-Soll) kommen aus
 * sifaBetriebsdatenLogic und werden nie still geschrieben.
 */
export const SifaBetriebsdatenFelder: React.FC<Props> = ({ sifa, onPatch, isReadOnly = false }) => {
  const [entwurf, setEntwurf] = useState<Entwurf>(() => entwurfAus(sifa));

  // Von aussen geaenderte Werte (Vorschlag-Knopf, anderes Geraet) uebernehmen.
  // Der eigene Blur-Commit landet hier ebenfalls — mit denselben Werten,
  // also ohne sichtbare Aenderung.
  useEffect(() => { setEntwurf(entwurfAus(sifa)); }, [sifa]);

  const setFeld = (feld: keyof Entwurf, wert: string) => setEntwurf(prev => ({ ...prev, [feld]: wert }));

  const commitText = (feld: 'branche' | 'berufsgenossenschaft' | 'bgMitgliedsnummer' | 'betriebsarzt' | 'sicherheitsbeauftragte') => {
    const wert = entwurf[feld].trim();
    if (wert === (sifa?.[feld] || '')) return;
    onPatch({ [feld]: wert || undefined });
  };

  const commitZahl = (feld: 'beschaeftigte' | 'einsatzzeitSollStunden' | 'ersthelfer') => {
    const wert = zahlAusEingabe(entwurf[feld]);
    if (wert === sifa?.[feld]) return;
    onPatch({ [feld]: wert });
  };

  const gruppe = sifa?.betreuungsgruppe;
  const gruppeInfo = BETREUUNGSGRUPPEN.find(g => g.id === gruppe);
  const vorschlag = vorschlagEinsatzzeit(gruppe, sifa?.beschaeftigte);
  const ehSoll = ersthelferSoll(sifa?.beschaeftigte);

  return (
    <div className="bg-teal-50/60 p-4 rounded-xl border border-teal-100 shadow-inner">
      <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3"><Factory className="w-4 h-4 text-teal-600" /> Betrieb</h4>
      <div className="space-y-3">
        <div className="space-y-1">
          <label className={labelClass}>Branche / Tätigkeit</label>
          <input type="text" value={entwurf.branche} onChange={e => setFeld('branche', e.target.value)} onBlur={() => commitText('branche')} readOnly={isReadOnly} className={inputClass} placeholder="z. B. Metallbau, Pflegeheim, Logistik" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className={labelClass}>Berufsgenossenschaft</label>
            <input type="text" value={entwurf.berufsgenossenschaft} onChange={e => setFeld('berufsgenossenschaft', e.target.value)} onBlur={() => commitText('berufsgenossenschaft')} readOnly={isReadOnly} className={inputClass} placeholder="z. B. BGHM, BG BAU, BGW" />
          </div>
          <div className="space-y-1">
            <label className={labelClass}>Mitgliedsnummer</label>
            <input type="text" value={entwurf.bgMitgliedsnummer} onChange={e => setFeld('bgMitgliedsnummer', e.target.value)} onBlur={() => commitText('bgMitgliedsnummer')} readOnly={isReadOnly} className={`${inputClass} font-mono`} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className={labelClass}>Beschäftigte</label>
            <input type="number" min={0} step={1} value={entwurf.beschaeftigte} onChange={e => setFeld('beschaeftigte', e.target.value)} onBlur={() => commitZahl('beschaeftigte')} readOnly={isReadOnly} className={`${inputClass} font-mono font-bold`} placeholder="Kopfzahl" />
          </div>
          <div className="space-y-1">
            <label className={labelClass}>Betreuungsgruppe</label>
            <select
              value={gruppe || ''}
              disabled={isReadOnly}
              onChange={e => onPatch({ betreuungsgruppe: (e.target.value || undefined) as SifaBetreuungsgruppe | undefined })}
              className={`${inputClass} font-medium disabled:bg-slate-100`}
            >
              <option value="">-- Nicht festgelegt --</option>
              {BETREUUNGSGRUPPEN.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-start gap-2 bg-white/70 p-2 rounded-lg text-[11px] text-slate-600 border border-teal-100">
          <Info className="w-3 h-3 mt-0.5 text-teal-600 shrink-0" />
          <span>{gruppeInfo ? gruppeInfo.hilfe : 'DGUV Vorschrift 2, Anlage 2: I = 2,5 h · II = 1,5 h · III = 0,5 h je Beschäftigtem und Jahr (Summe SiFa + Betriebsarzt).'}</span>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className={labelClass}>Einsatzzeit Soll (h / Jahr)</label>
            {!isReadOnly && (
              <button
                type="button"
                disabled={vorschlag === null}
                onClick={() => { if (vorschlag !== null) onPatch({ einsatzzeitSollStunden: vorschlag }); }}
                title={vorschlag === null ? 'Beschäftigte und Betreuungsgruppe zuerst eintragen' : `Vorschlag: ${vorschlag} h`}
                className="text-[11px] font-bold text-teal-700 hover:text-teal-900 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-3 h-3" /> Vorschlag aus Gruppe{vorschlag !== null ? ` (${vorschlag} h)` : ''}
              </button>
            )}
          </div>
          <input type="number" min={0} step={0.5} value={entwurf.einsatzzeitSollStunden} onChange={e => setFeld('einsatzzeitSollStunden', e.target.value)} onBlur={() => commitZahl('einsatzzeitSollStunden')} readOnly={isReadOnly} className={`${inputClass} font-mono font-bold`} placeholder="vereinbarte Stunden" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className={labelClass}>Bestellt am (§ 5 ASiG)</label>
            <input type="date" value={sifa?.bestelltAm || ''} onChange={e => onPatch({ bestelltAm: e.target.value || undefined })} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="space-y-1">
            <label className={labelClass}>Betriebsarzt</label>
            <input type="text" value={entwurf.betriebsarzt} onChange={e => setFeld('betriebsarzt', e.target.value)} onBlur={() => commitText('betriebsarzt')} readOnly={isReadOnly} className={inputClass} placeholder="Name / Praxis" />
          </div>
        </div>

        <div className="space-y-1">
          <label className={labelClass}>Sicherheitsbeauftragte</label>
          <input type="text" value={entwurf.sicherheitsbeauftragte} onChange={e => setFeld('sicherheitsbeauftragte', e.target.value)} onBlur={() => commitText('sicherheitsbeauftragte')} readOnly={isReadOnly} className={inputClass} placeholder="Namen, durch Komma getrennt" />
        </div>

        <div className="grid grid-cols-2 gap-3 items-end">
          <div className="space-y-1">
            <label className={labelClass}>Ersthelfer</label>
            <input type="number" min={0} step={1} value={entwurf.ersthelfer} onChange={e => setFeld('ersthelfer', e.target.value)} onBlur={() => commitZahl('ersthelfer')} readOnly={isReadOnly} className={`${inputClass} font-mono font-bold`} />
            <p className="text-[11px] text-slate-500 px-1">
              {ehSoll !== null
                ? <>Soll: <span className={`font-bold ${(sifa?.ersthelfer ?? 0) < ehSoll ? 'text-amber-700' : 'text-slate-700'}`}>{ehSoll}</span> (5 % — Verwaltung/Handel; sonstige Betriebe 10 %; bis 20 Beschäftigte einer)</>
                : 'Soll ergibt sich aus der Beschäftigtenzahl (DGUV V1 § 26).'}
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none pb-6">
            <input type="checkbox" checked={sifa?.betriebsrat === true} disabled={isReadOnly} onChange={e => onPatch({ betriebsrat: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
            <span className="text-xs font-bold text-slate-700">Betriebsrat vorhanden</span>
          </label>
        </div>
      </div>
    </div>
  );
};
